// Font asset URLs. Importing with Vite's `?url` suffix only puts the URL string
// in the bundle and emits the .ttf as a separate static asset — the font bytes
// are NOT downloaded until we explicitly `fetch()` them below. This is what
// makes the loading lazy/on-demand.
import notoSansBoldUrl from '@/assets/fonts/NotoSans-Bold.ttf?url';
import notoSansRegularUrl from '@/assets/fonts/NotoSans-Regular.ttf?url';
import notoSansJpUrl from '@/assets/fonts/NotoSansJP-Regular.ttf?url';
import notoSansScUrl from '@/assets/fonts/NotoSansSC-Regular.ttf?url';

import type jsPDF from 'jspdf';

/**
 * Font families we register with jsPDF. Each value is the name passed to
 * `doc.setFont(family, style)` and to autoTable's `styles.font`.
 *
 * Why this exists at all: jsPDF only ships the "Standard 14" Latin-1 base fonts
 * (helvetica/times/courier). They have NO glyphs for CJK/Vietnamese, so non-Latin
 * text renders as mojibake. The fix is to embed real Unicode fonts (Noto) via the
 * official `addFileToVFS` + `addFont` API and select the right one per document.
 */
export const PDF_FONT = {
  /** Latin + Latin-Extended + Vietnamese (EN, NB, NN, SV, DE, ES, FR, CA, VI). */
  base: 'NotoSans',
  /** Chinese (Han ideographs, also covers ASCII for mixed content). */
  chinese: 'NotoSansSC',
  /** Japanese (Kanji + Hiragana + Katakana, also covers ASCII). */
  japanese: 'NotoSansJP',
} as const;

export type PdfFontFamily = (typeof PDF_FONT)[keyof typeof PDF_FONT];

type ScriptKind = 'latin' | 'han' | 'kana';

// Cache the decoded base64 per asset URL so a given font is fetched + decoded at
// most once per browser session, no matter how many exports the user runs.
const base64Cache = new Map<string, Promise<string>>();

/**
 * Convert an ArrayBuffer to a base64 string in a browser-safe, stack-safe way.
 * `btoa(String.fromCharCode(...bytes))` blows the call stack on multi-MB CJK
 * fonts, so we chunk it.
 */
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000; // 32 KB per chunk
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

const loadFontBase64 = (url: string): Promise<string> => {
  const cached = base64Cache.get(url);
  if (cached) return cached;

  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to load PDF font (${res.status}): ${url}`);
      return res.arrayBuffer();
    })
    .then(arrayBufferToBase64)
    .catch((err) => {
      // Don't poison the cache on failure — allow a later retry.
      base64Cache.delete(url);
      throw err;
    });

  base64Cache.set(url, promise);
  return promise;
};

/**
 * Decide which script a document is in by scanning its text. The meeting/
 * transcript data carries no language field, so we detect from the characters
 * themselves (the standard approach):
 *  - any Kana => use Japanese font (covers Kanji + Hiragana + Katakana + ASCII)
 *  - else any Han => use Chinese font
 *  - else Latin font
 *
 * Note: If document contains both Han and Kana, Japanese font is chosen first
 * because it covers both scripts plus ASCII, making it the safest for mixed CJK.
 */
const detectScript = (text: string): ScriptKind => {
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    // Hiragana, Katakana, Katakana Phonetic Extensions
    // Check Kana first: if found, return immediately (covers mixed Han+Kana)
    if ((cp >= 0x3040 && cp <= 0x30ff) || (cp >= 0x31f0 && cp <= 0x31ff)) {
      return 'kana';
    }
  }

  // Kana not found, check for Han
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    // CJK Unified (+ Ext A), Compatibility Ideographs, CJK Ext B
    if (
      (cp >= 0x3400 && cp <= 0x4dbf) ||
      (cp >= 0x4e00 && cp <= 0x9fff) ||
      (cp >= 0xf900 && cp <= 0xfaff) ||
      (cp >= 0x20000 && cp <= 0x2a6df)
    ) {
      return 'han';
    }
  }

  // Default to Latin
  return 'latin';
};

const registerFont = (
  doc: jsPDF,
  vfsName: string,
  base64: string,
  family: string,
  style: 'normal' | 'bold',
): void => {
  doc.addFileToVFS(vfsName, base64);
  doc.addFont(vfsName, family, style);
};

/**
 * Ensure the jsPDF document has an embedded Unicode font registered that can
 * render `sampleText`, and return the family name to use for every `setFont`
 * call and autoTable `styles.font`.
 *
 * Only the font actually needed is fetched:
 *  - Latin documents pull NotoSans Regular + Bold (~0.8 MB, cached).
 *  - Chinese/Japanese documents pull the single matching CJK file (~2.3 MB),
 *    which also covers ASCII, so it is used for the whole document.
 *
 * CJK files ship a single weight, so we register the same file for both the
 * `normal` and `bold` slots — bold headers stay legible (just not heavier),
 * which is the correct trade (correctness over faux-bold).
 */
export const ensurePdfFont = async (doc: jsPDF, sampleText: string): Promise<PdfFontFamily> => {
  const script = detectScript(sampleText);

  if (script === 'kana' || script === 'han') {
    const family = script === 'kana' ? PDF_FONT.japanese : PDF_FONT.chinese;
    const url = script === 'kana' ? notoSansJpUrl : notoSansScUrl;
    const vfsName = `${family}-Regular.ttf`;
    const base64 = await loadFontBase64(url);
    registerFont(doc, vfsName, base64, family, 'normal');
    registerFont(doc, vfsName, base64, family, 'bold');
    doc.setFont(family, 'normal');
    return family;
  }

  const [regular, bold] = await Promise.all([
    loadFontBase64(notoSansRegularUrl),
    loadFontBase64(notoSansBoldUrl),
  ]);
  registerFont(doc, 'NotoSans-Regular.ttf', regular, PDF_FONT.base, 'normal');
  registerFont(doc, 'NotoSans-Bold.ttf', bold, PDF_FONT.base, 'bold');
  doc.setFont(PDF_FONT.base, 'normal');
  return PDF_FONT.base;
};
